import { useState, useEffect, useCallback, useRef } from 'react';

interface UseAsyncResourceOptions<T> {
  enabled?: boolean;
  initialData?: T;
  onError?: (error: unknown) => void;
}

interface UseAsyncResourceReturn<T> {
  data: T | undefined;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setData: React.Dispatch<React.SetStateAction<T | undefined>>;
}

export function useAsyncResource<T>(
  loader: () => Promise<T>,
  deps: any[],
  options?: UseAsyncResourceOptions<T>
): UseAsyncResourceReturn<T> {
  const { enabled = true, initialData, onError } = options || {};

  const [data, setData] = useState<T | undefined>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const loadIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) return;

    const currentLoadId = ++loadIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const result = await loader();

      if (!mountedRef.current) return;
      if (currentLoadId !== loadIdRef.current) return;

      setData(result);
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      if (currentLoadId !== loadIdRef.current) return;

      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);

      if (onError) {
        onError(err);
      }
    } finally {
      if (mountedRef.current && currentLoadId === loadIdRef.current) {
        setLoading(false);
      }
    }
  }, [loader, enabled, onError]);

  useEffect(() => {
    mountedRef.current = true;
    load();

    return () => {
      mountedRef.current = false;
    };
  }, [...deps, enabled]);

  return {
    data,
    loading,
    error,
    reload: load,
    setData,
  };
}
